import { Component, OnInit, ViewChild } from '@angular/core';
import { LabeledInputComponent } from "../../../../../shared/components/labeled-input/labeled-input.component";
import { PrimaryButtonComponent } from "../../../../../shared/components/primary-button/primary-button.component";
import { TextareaInputComponent } from "../../../../../shared/components/textarea-input/textarea-input.component";
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { OnboardingService } from '../../../../../core/services/onboarding.service';
import { Router } from '@angular/router';
import { AuthValidatorService } from '../../../../../core/services/validators/auth-validator.service';
import { CompanyService } from '../../../../../core/api/company.service';
import { firstValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastComponent } from '../../../../../shared/components/toast/toast.component';
import { UserDataService } from '../../../../../core/services/user-data.service';
import { UserService } from '../../../../../core/api/user.service';
import { UserSettings } from '../../../../../core/interfaces/user-interfaces';
import { ApiErrorService } from '../../../../../core/api/api-error.service';
import { AtecoCode } from '../../../../../core/interfaces/company-interfaces';
import { FormsModule } from '@angular/forms';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { CommonModule } from '@angular/common';
import { CustomAutocompleteComponent } from "../../../../../shared/components/custom-autocomplete/custom-autocomplete.component";
import { LanguageService } from '../../../../../core/services/language.service';

@Component({
  selector: 'app-onboarding-step-two',
  standalone: true,
  imports: [LabeledInputComponent, PrimaryButtonComponent, TextareaInputComponent, TranslocoPipe, ToastComponent, FormsModule, AutoCompleteModule, CommonModule, CustomAutocompleteComponent],
  templateUrl: './onboarding-step-two.component.html',
  styleUrl: './onboarding-step-two.component.css'
})
export class OnboardingStepTwoComponent implements OnInit {
  // Text Inputs
  companyNameInput: string = '';
  companyNameInputError: string | null = null;

  industryCodeInput: string = '';
  industryCodeInputError: string | null = null;

  selectedAtecoCode: AtecoCode | null = null;
  allAtecoCodes: AtecoCode[] = [];
  atecoCodesLoaded: boolean = false;

  userLanguage: string = 'en';

  aboutUsInput: string = '';
  aboutUsInputError: string | null = null;

  continueClicked: boolean = false;

  @ViewChild(ToastComponent) toast!: ToastComponent;

  constructor(private onboardingService: OnboardingService, private router: Router, private authValidator: AuthValidatorService, private companyService: CompanyService, private userData: UserDataService, private userService: UserService, private apiError: ApiErrorService, private languageService: LanguageService, private translocoService: TranslocoService) { }

  ngOnInit(): void {
    // Load ateco codes
    this.loadAtecoCodes();

    // Go to dashboard (activate auth guard) if status step is not 2 
    if (this.onboardingService.checkStatus() != 2) {
      this.router.navigate(['/dashboard']);
      return;
    }

    this.userLanguage = this.languageService.getCurrentUILanguage();
  }
  
  async continueButtonClick() {
    // Prevent multiple clicks
    if (this.continueClicked) {
      return;
    }
    this.continueClicked = true;

    // Trim inputs
    const companyName = this.companyNameInput.trim();
    const industryCode = this.selectedAtecoCode?.code ?? '';
    const aboutUs = this.aboutUsInput.trim();

    // Validate inputs
    const companyNameError = this.authValidator.validateCompanyName(companyName);
    const industryCodeError = (this.selectedAtecoCode == null) ? this.translocoService.translate('auth.select_ateco_code') || 'error' : null;
    const aboutUsError = this.authValidator.validateAboutUs(aboutUs);

    this.companyNameInputError = companyNameError;
    this.industryCodeInputError = industryCodeError;
    this.aboutUsInputError = aboutUsError;

    if (companyNameError || industryCodeError || aboutUsError) {
      this.continueClicked = false;
      return;
    }

    // Check if company already exists
    if (this.userData.getUser()?.company) {
      this.router.navigate(["/auth/onboarding/"], { queryParams: { step: 3 } });
      this.continueClicked = false;
      return;
    }

    // Creating company
    let companyResponse = undefined;
    try {
      companyResponse = await firstValueFrom(this.companyService.addCompany(companyName, industryCode));
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      var showError: boolean = true;

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';

        if (error.error?.code === 'SVC-4008') {
          this.toast.showToast('shared.error', 'shared.toast.company_already_exists', 'error', 4000, true);
          showError = false;
        }
      }

      if (showError) {
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      }
      this.continueClicked = false;
      return;
    }

    // Load user data and check if company is added
    await this.userData.loadUserData();
    if (!this.userData.getUser()?.company) {
      this.toast.showToast("shared.error", "shared.toast.unexpected_error", 'error', 3000);
      this.continueClicked = false;
      return;
    }

    // Add new data to user settings
    let user = this.userData.getUser();
    if (!user?.settings) {
      this.toast.showToast("shared.error", "shared.toast.unexpected_error", 'error', 3000);
      this.continueClicked = false;
      return;
    }
    user.settings.feedback_hear_about = aboutUs;

    // Sending new data
    let updateUserSettingResponse = undefined;
    try {
      updateUserSettingResponse = await firstValueFrom(this.userService.updateUserSettings(user.settings.id, user.settings));
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      this.continueClicked = false;
      return;
    }

    // Saving new user settings
    user.settings = updateUserSettingResponse as UserSettings;
    this.userData.setUser(user);

    this.router.navigate(["/auth/onboarding/"], { queryParams: { step: 3 } });
    this.continueClicked = false;
  }

  loadAtecoCodes() {
    this.companyService.getAtecoCodes().subscribe({
      next: (response) => {
        if (response) {
          this.allAtecoCodes = response;
          this.atecoCodesLoaded = true;
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      },
      complete: () => {

      }
    });
  }

  labelRendererFn = (item: any) => {
    const lang = this.userLanguage
    let description;
    if (lang === 'it'){
      description = item?.description_italian
    }
    else {
      description = item?.description_english
    }
    if (description) return `${item.code} - ${description}`;
    else return `${item.code}`;
  };
}
